import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { KcStatusTimelineStatus } from '../../../types/radar';
import { formatTimeOnly } from '../../../utils/formatUtils';

import './_vertical-kc-timeline.scss';

export type VerticalKcTimelineStatus = KcStatusTimelineStatus | 'INITIAL';

export type VerticalKcTimelineEntry = {
  createdAt: string;
  status: VerticalKcTimelineStatus;
};

type VerticalKcTimelineProps = {
  entries: VerticalKcTimelineEntry[];
};

type TimelineItem = {
  dateLabel: string;
  id: string;
  isLast: boolean;
  statusLabelKey: string;
  timeLabel: string;
};

const TIMELINE_STATUS_KEYS: Record<VerticalKcTimelineStatus, string> = {
  INITIAL: 'student_kc_progress_timeline_status_initial',
  MASTERED: 'student_kc_progress_timeline_status_mastered',
  NEEDS_PROOF: 'student_kc_progress_timeline_status_needs_proof',
  ROLLBACK: 'student_kc_progress_timeline_status_rollback',
  ROLLBACK_RESOLVED: 'student_kc_progress_timeline_status_rollback_resolved',
};

function formatTimelineDate(dateString: string): string {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * VerticalKcTimeline - Renders a vertical timeline of KC status changes.
 * The first entry (synthetic INITIAL) marks the start of practice and the
 * last entry is highlighted as the most recent state.
 */
function VerticalKcTimeline({ entries }: VerticalKcTimelineProps): JSX.Element | null {
  const { t } = useTranslation(['chatbot']);

  const timelineItems = useMemo<TimelineItem[]>(() => {
    if (entries.length === 0) return [];
    const lastIndex = entries.length - 1;

    return entries.map((entry, index) => ({
      dateLabel: formatTimelineDate(entry.createdAt),
      id: `${entry.createdAt}-${entry.status}-${index}`,
      isLast: index === lastIndex,
      statusLabelKey: TIMELINE_STATUS_KEYS[entry.status],
      timeLabel: formatTimeOnly(entry.createdAt),
    }));
  }, [entries]);

  if (timelineItems.length === 0) return null;

  return (
    <ul className="vertical-kc-timeline">
      {timelineItems.map((item) => (
        <li className={`vertical-kc-timeline__item${item.isLast ? ' vertical-kc-timeline__item--last' : ''}`} key={item.id}>
          <div className="vertical-kc-timeline__date">
            <span className="vertical-kc-timeline__date-day">{item.dateLabel}</span>
            <span className="vertical-kc-timeline__date-time">{item.timeLabel}</span>
          </div>
          <div className="vertical-kc-timeline__marker">
            <span className="vertical-kc-timeline__dot" />
          </div>
          <span className="vertical-kc-timeline__status-label">{t(`chatbot:${item.statusLabelKey}`)}</span>
        </li>
      ))}
    </ul>
  );
}

export default VerticalKcTimeline;
